import type { ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  FormLabel,
  Input,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { Lock01 } from '@kula-ai/icons-core';
import { hasUsableValue } from '@/api/customFields/formulaWireUnits';
import { colors } from '@/theme/foundations/colors';
import { formulaSegments } from '@/utils/formula/formulaHighlight';
import { SEGMENT_COLOR } from '@/utils/formula/formulaHighlight';
import { useCalculatedValue } from './useCalculatedValue';

export type CalculatedFieldState = 'ok' | 'blocked' | 'error';

export interface CalculatedFieldValueProps {
  label: string;
  formula: string;
  // Whatever the form holds right now, keyed the way the config names fields.
  values: Record<string, unknown>;
  // Maps a formula token to the form field holding its value.
  fieldNameOf: (token: string) => string;
  // The backend's verdict on the saved rule. Omitted — nothing sends it yet —
  // means show whatever the server computes now.
  state?: CalculatedFieldState;
  // The fields a 'blocked' rule is still waiting on, named for the recruiter.
  // The blocking field draws its own red treatment elsewhere, from this list.
  blockedBy?: string[];
  // Locked shows a padlock and disables the input; unlocked lets the recruiter
  // type over the calculated value.
  locked?: boolean;
  // The recruiter's override, once they have typed one.
  value?: string | number | null;
  onChange?: (value: string) => void;
  // A unit or currency picker, for the field kinds that have one.
  rightElement?: ReactNode;
  subjectType?: string;
  // The field being calculated, so the server checks the result fits it.
  kind?: string;
  fieldId?: string | number;
}

// Shown when the formula evaluates to nothing and no state says why.
export const NO_VALUE = '—';

const FORMULA_TOOLTIP_LABEL = 'Current formula:';
const FORMULA_TAG_LABEL = 'Formula';
// What a field that cannot compute shows in place of a number.
const BLOCKED_VALUE = 'Null';

const blockedMessage = (fieldName: string) =>
  `Fill in the dependent "${fieldName}" field to see this value`;

// The same colouring the settings editor uses, so one formula reads the same
// in both places.
const HighlightedFormula = ({ formula }: { formula: string }) => (
  <Box
    mt="1"
    px="2"
    py="1"
    bg="secondary-25"
    borderWidth="1px"
    borderColor="border-color"
    borderRadius="md"
    fontFamily="mono"
    fontSize="xs"
    // The design's formula is nested and indented, so both must survive.
    whiteSpace="pre-wrap"
    wordBreak="break-word"
    data-test-id="calculated-formula"
  >
    {formulaSegments(formula, []).map((segment) => (
      <span
        key={`${segment.start}-${segment.text}`}
        style={{ color: SEGMENT_COLOR[segment.kind] }}
      >
        {segment.text}
      </span>
    ))}
  </Box>
);

// The formula behind the number, so the recruiter can see what produced it.
const FormulaTooltip = ({
  formula,
  focusable,
  children,
}: {
  formula: string;
  focusable: boolean;
  children: ReactNode;
}) => (
  // 700ms default reads as broken, so the repo pins hover tooltips to 150ms.
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        {/* tabIndex only when nothing inside already takes focus. */}
        <Box
          as="span"
          display="block"
          tabIndex={focusable ? undefined : 0}
          data-test-id="calculated-field-trigger"
        >
          {children}
        </Box>
      </TooltipTrigger>
      <TooltipContent variant="light" side="top" collisionPadding={12}>
        <Box maxW="320px">
          <Text variant="small">{FORMULA_TOOLTIP_LABEL}</Text>
          <HighlightedFormula formula={formula} />
        </Box>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Red on the error path; the repo's red is the pastel-1 pair, not an error scale.
const FormulaTag = ({
  locked,
  invalid,
}: {
  locked: boolean;
  invalid: boolean;
}) => (
  <Tag
    size="sm"
    label={FORMULA_TAG_LABEL}
    leftElement={locked ? <Lock01 size={12} /> : undefined}
    // Tag spreads `style` after its own, so `background` must go here too.
    style={
      invalid
        ? {
            background: colors['pastel-1-light'],
            color: colors['pastel-1-dark'],
          }
        : undefined
    }
    data-test-id="calculated-formula-tag"
  />
);

// A formula field, rendered like every other field on the form: a bordered
// input carrying the calculated value, tagged "Formula".
export const CalculatedFieldValue = ({
  label,
  formula,
  values,
  fieldNameOf,
  state,
  blockedBy,
  locked = true,
  value,
  onChange,
  rightElement,
  subjectType,
  kind,
  fieldId,
}: CalculatedFieldValueProps) => {
  const { value: computed, isFetching } = useCalculatedValue({
    formula,
    values,
    fieldNameOf,
    subjectType,
    kind,
    fieldId,
  });
  // A 'blocked' or 'error' rule can still carry a value from an earlier save,
  // so the state decides whether a value may be shown at all.
  const usable = hasUsableValue({ state: state ?? 'ok', value: computed });
  // 'error' is an authoring fault the design says nothing about, so it borrows
  // the blocked treatment minus the message — no field to point the person at.
  const invalid = state === 'blocked' || state === 'error';
  // A locked field never takes an override: generateCustomFormConfig always
  // passes the saved value, so treating it as one would freeze a stale number.
  const overridden =
    !locked && value !== null && value !== undefined && value !== '';
  const editable = !locked && !invalid;

  const inputValue = () => {
    if (invalid) return BLOCKED_VALUE;
    if (overridden) return String(value);
    return usable ? (computed ?? NO_VALUE) : NO_VALUE;
  };

  return (
    <Box mt="3" data-test-id="calculated-field">
      <Flex align="center" gap="2" mb="1">
        <FormLabel className="mb-0 block" error={invalid}>
          {label}
        </FormLabel>
        <FormulaTag locked={locked} invalid={invalid} />
      </Flex>
      <FormulaTooltip formula={formula} focusable={editable}>
        <Input
          aria-label={label}
          // The server is recomputing, so the number on screen is a moment behind.
          aria-busy={isFetching}
          value={inputValue()}
          error={invalid}
          disabled={!editable}
          readOnly={!editable}
          rightElement={rightElement}
          onChange={
            editable ? (event) => onChange?.(event.target.value) : undefined
          }
          data-test-id="calculated-value"
        />
      </FormulaTooltip>
      {state === 'blocked' && blockedBy?.[0] ? (
        // kula Text drops data-* attributes, so the hook sits on the wrapper.
        <Box mt="1" data-test-id="calculated-blocked">
          <Text variant="small" style={{ color: colors['pastel-1-dark'] }}>
            {blockedMessage(blockedBy[0])}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};
